import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { CopyButton } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine/engine-service';
import { WebhookDelivery } from '@/types/alert';
import { formatRequestBody } from './webhook-delivery-payload';
import { WebhookDeliveryCodeBlock } from './WebhookDeliveryCodeBlock';
import { WebhookDeliveryDetailField } from './WebhookDeliveryDetailField';

interface WebhookDeliveryDetailsProps {
    alertRuleId: string;
    alertRuleActionId: string;
    delivery: WebhookDelivery;
    isExpanded: boolean;
}

export function WebhookDeliveryDetails({
    alertRuleId,
    alertRuleActionId,
    delivery,
    isExpanded,
}: WebhookDeliveryDetailsProps) {
    const { t } = useTranslation(I18nNamespace.ALERT_RULES);
    const { data: detail, error } = useSWR(
        isExpanded
            ? ['webhook-delivery', alertRuleId, alertRuleActionId, delivery.id]
            : null,
        () =>
            EngineService.getWebhookDelivery(
                alertRuleId,
                alertRuleActionId,
                delivery.id,
            ),
        { revalidateOnFocus: false },
    );
    const requestBody = error
        ? t('webhookDeliveries.loadFailed')
        : detail
          ? formatRequestBody(detail.requestBody)
          : t('webhookDeliveries.loadingRequestBody');

    return (
        <div className="flex flex-col gap-3">
            <WebhookDeliveryDetailField label={t('webhookDeliveries.url')}>
                <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate font-geist-mono text-xs text-grey-200">
                        {delivery.url}
                    </span>
                    <CopyButton value={delivery.url} />
                </span>
            </WebhookDeliveryDetailField>
            <WebhookDeliveryDetailField
                label={t('webhookDeliveries.columnRequestId')}
            >
                <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate font-geist-mono text-xs text-grey-200">
                        {delivery.requestId}
                    </span>
                    <CopyButton value={delivery.requestId} />
                </span>
            </WebhookDeliveryDetailField>
            {delivery.error && (
                <WebhookDeliveryDetailField
                    label={t('webhookDeliveries.error')}
                >
                    <span className="font-geist-mono text-xs text-danger-200 break-words">
                        {delivery.error}
                    </span>
                </WebhookDeliveryDetailField>
            )}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <WebhookDeliveryCodeBlock
                    title={t('webhookDeliveries.requestBody')}
                    content={requestBody}
                    copyValue={detail?.requestBody}
                />
                <WebhookDeliveryCodeBlock
                    title={t('webhookDeliveries.responseBody')}
                    content={
                        delivery.responseSnippet ||
                        t('webhookDeliveries.noResponseBody')
                    }
                    copyValue={delivery.responseSnippet}
                />
            </div>
        </div>
    );
}
